import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { evaluateRules, getSimplifiedOutput } from '@/lib/rules/engine';
import type {
  PayerAuthRule,
  PayerRuleTemplate,
  DocumentType,
  RulesEngineInput,
  RulesEngineOutput,
  RuleFormInput,
} from '@/lib/rules/types';

export function usePayerRules() {
  const [rules, setRules] = useState<PayerAuthRule[]>([]);
  const [templates, setTemplates] = useState<PayerRuleTemplate[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all active rules with optional filters
  const fetchRules = useCallback(async (filters?: {
    payer_id?: string;
    injury_type?: 'bwc' | 'commercial';
    cpt_code?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('payer_auth_rules')
        .select(`
          *,
          payer:payers(id, name, payer_id)
        `)
        .eq('is_active', true)
        .order('cpt_code', { ascending: true });

      if (filters?.payer_id) {
        query = query.eq('payer_id', filters.payer_id);
      }
      if (filters?.injury_type) {
        query = query.eq('injury_type', filters.injury_type);
      }
      if (filters?.cpt_code) {
        query = query.ilike('cpt_code', `${filters.cpt_code}%`);
      }

      const { data, error: queryError } = await query.limit(500);
      
      if (queryError) throw queryError;
      setRules((data || []) as unknown as PayerAuthRule[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: queryError } = await supabase
        .from('payer_rule_templates')
        .select('*')
        .order('injury_type', { ascending: true })
        .order('name', { ascending: true });
      
      if (queryError) throw queryError;
      setTemplates((data || []) as unknown as PayerRuleTemplate[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch document types
  const fetchDocumentTypes = useCallback(async () => {
    try {
      const { data, error: queryError } = await supabase
        .from('document_types')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });
      
      if (queryError) throw queryError;
      setDocumentTypes((data as DocumentType[]) || []);
    } catch (err) {
      console.error('Error fetching document types:', err);
    }
  }, []);

  // Create a new rule
  const createRule = useCallback(async (input: RuleFormInput): Promise<PayerAuthRule | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: insertError } = await supabase
        .from('payer_auth_rules')
        .insert({
          payer_id: input.payer_id || null,
          plan_name: input.plan_name || null,
          cpt_code: input.cpt_code,
          modifier: input.modifier || null,
          injury_type: input.injury_type,
          site_of_service: input.site_of_service || null,
          diagnosis_pattern: input.diagnosis_pattern || null,
          auth_requirement: input.auth_requirement,
          auth_condition_description: input.auth_condition_description || null,
          primary_submission_lane: input.primary_submission_lane,
          fallback_submission_lane: input.fallback_submission_lane || null,
          payer_portal_url: input.payer_portal_url || null,
          payer_fax_number: input.payer_fax_number || null,
          payer_phone_number: input.payer_phone_number || null,
          sla_hours: input.sla_hours,
          urgent_sla_hours: input.urgent_sla_hours || null,
          initial_follow_up_hours: input.initial_follow_up_hours,
          follow_up_cadence_hours: input.follow_up_cadence_hours,
          required_documents: input.required_documents,
          clinical_criteria: input.clinical_criteria || null,
          special_instructions: input.special_instructions || null,
          effective_date: input.effective_date,
          expiration_date: input.expiration_date || null,
          is_active: true,
          version: 1,
        })
        .select()
        .single();
      
      if (insertError) throw insertError;
      return data as unknown as PayerAuthRule;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create rule');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing rule (creates new version)
  const updateRule = useCallback(async (
    ruleId: string, 
    input: Partial<RuleFormInput>
  ): Promise<PayerAuthRule | null> => {
    setLoading(true);
    setError(null);
    try {
      // First, fetch the current rule
      const { data: currentRule, error: fetchError } = await supabase
        .from('payer_auth_rules')
        .select('*')
        .eq('id', ruleId)
        .single();
      
      if (fetchError) throw fetchError;

      // Deactivate the current rule
      await supabase
        .from('payer_auth_rules')
        .update({ is_active: false })
        .eq('id', ruleId);

      // Create new version
      const { data: newRule, error: insertError } = await supabase
        .from('payer_auth_rules')
        .insert({
          ...currentRule,
          ...input,
          id: undefined, // Let DB generate new ID
          version: (currentRule.version || 1) + 1,
          superseded_by: undefined,
          is_active: true,
          created_at: undefined,
          updated_at: undefined,
        })
        .select()
        .single();
      
      if (insertError) throw insertError;

      // Update old rule to point to new one
      await supabase
        .from('payer_auth_rules')
        .update({ superseded_by: newRule.id })
        .eq('id', ruleId);

      return newRule as unknown as PayerAuthRule;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update rule');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Deactivate a rule
  const deactivateRule = useCallback(async (ruleId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('payer_auth_rules')
        .update({ is_active: false })
        .eq('id', ruleId);
      
      if (updateError) throw updateError;
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate rule');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Evaluate rules for an authorization
  const evaluate = useCallback(async (input: RulesEngineInput): Promise<RulesEngineOutput> => {
    return evaluateRules(input);
  }, []);

  // Get simplified output for surgeon console
  const evaluateSimplified = useCallback(async (input: RulesEngineInput) => {
    const output = await evaluateRules(input);
    return {
      full: output,
      simplified: getSimplifiedOutput(output),
    };
  }, []);

  return {
    rules,
    templates,
    documentTypes,
    loading,
    error,
    fetchRules,
    fetchTemplates,
    fetchDocumentTypes,
    createRule,
    updateRule,
    deactivateRule,
    evaluate,
    evaluateSimplified,
  };
}
